/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import PropTypes from 'prop-types';
import { DatePicker as AntDatePicker } from 'antd';
import moment from 'moment';

const DEFAULT_FORMAT = 'YYYY-MM-DD';

const formatsForParsing = (format) => {
  if (!format) return [DEFAULT_FORMAT];
  if (Array.isArray(format)) return format.filter((item) => typeof item === 'string');
  return typeof format === 'string' ? [format] : [DEFAULT_FORMAT];
};

const firstFormat = (formatList) => formatList[0] || DEFAULT_FORMAT;

const hasYearToken = (formatList) => (
  formatList.some((format) => /Y/.test(format.replace(/\[[^\]]*]/g, '')))
);

const sameDate = (left, right) => (
  moment.isMoment(left)
  && moment.isMoment(right)
  && left.isValid()
  && right.isValid()
  && left.isSame(right, 'day')
);

const DatePicker = React.forwardRef(({
  format,
  onBlur,
  onChange,
  value,
  defaultValue,
  disabledDate,
  showTime,
  picker,
  ...props
}, ref) => {
  const rawInputRef = React.useRef('');
  const userTypedRef = React.useRef(false);
  const wrapperRef = React.useRef(null);
  const calendarMouseDownRef = React.useRef(false);
  const justCommittedRef = React.useRef(false);

  React.useEffect(() => {
    const handleDocumentMouseDown = (event) => {
      if (event.target?.closest?.('.ant-picker-dropdown')) {
        calendarMouseDownRef.current = true;
        window.setTimeout(() => {
          calendarMouseDownRef.current = false;
        }, 0);
      }
    };

    document.addEventListener('mousedown', handleDocumentMouseDown, true);
    return () => document.removeEventListener('mousedown', handleDocumentMouseDown, true);
  }, []);

  const markJustCommitted = () => {
    justCommittedRef.current = true;
    window.setTimeout(() => {
      justCommittedRef.current = false;
    }, 0);
  };

  const handleInputCapture = (event) => {
    if (event.target?.tagName === 'INPUT') {
      rawInputRef.current = event.target.value || '';
      userTypedRef.current = true;
    }
  };

  const handleChange = (date, dateString) => {
    rawInputRef.current = '';
    userTypedRef.current = false;
    markJustCommitted();
    if (onChange) onChange(date, dateString);
  };

  const handleBlurCapture = (event) => {
    const eventText = event?.target?.value || '';
    // rc-picker rewrites the input before blur fires -- it blanks the field in
    // some paths and, when the blur came from a click outside, resetText()
    // restores the previously committed text. So the typed text is only
    // trustworthy from our own input capture. Reading rawInputRef only when the
    // user actually typed keeps an emptied field from being read back as the
    // restored value, which silently discarded deliberate clears.
    const rawText = (userTypedRef.current ? rawInputRef.current : eventText).trim();
    const currentValue = value || defaultValue;
    const formatList = formatsForParsing(format);
    const { relatedTarget } = event;
    const focusMovedInsidePicker = relatedTarget && (
      wrapperRef.current?.contains(relatedTarget)
      || relatedTarget.closest?.('.ant-picker-dropdown')
    );
    const shouldCommitTypedDate = (
      !justCommittedRef.current
      && !calendarMouseDownRef.current
      && !focusMovedInsidePicker
      && hasYearToken(formatList)
      && !showTime
      && (!picker || picker === 'date')
    );

    if (shouldCommitTypedDate && onChange) {
      if (!rawText) {
        if (currentValue) handleChange(null, '');
      } else {
        const parsedDate = moment(rawText, formatList, true);

        if (
          parsedDate.isValid()
          && !sameDate(parsedDate, currentValue)
          && (!disabledDate || !disabledDate(parsedDate))
        ) {
          handleChange(parsedDate, parsedDate.format(firstFormat(formatList)));
        }
      }
    }

    rawInputRef.current = '';
    userTypedRef.current = false;
    if (onBlur) onBlur(event);
  };

  return (
    <span
      ref={wrapperRef}
      style={{ display: 'contents' }}
      onInputCapture={handleInputCapture}
      onBlurCapture={handleBlurCapture}
    >
      <AntDatePicker
        ref={ref}
        {...props}
        format={format}
        onChange={handleChange}
        value={value}
        defaultValue={defaultValue}
        disabledDate={disabledDate}
        showTime={showTime}
        picker={picker}
      />
    </span>
  );
});

DatePicker.displayName = 'DatePicker';

DatePicker.RangePicker = AntDatePicker.RangePicker;

DatePicker.propTypes = {
  format: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.func])),
    PropTypes.func,
  ]),
  onBlur: PropTypes.func,
  onChange: PropTypes.func,
  value: PropTypes.object,
  defaultValue: PropTypes.object,
  disabledDate: PropTypes.func,
  showTime: PropTypes.oneOfType([PropTypes.bool, PropTypes.object]),
  picker: PropTypes.string,
};

DatePicker.defaultProps = {
  format: undefined,
  onBlur: undefined,
  onChange: undefined,
  value: undefined,
  defaultValue: undefined,
  disabledDate: undefined,
  showTime: undefined,
  picker: undefined,
};

export default DatePicker;
